import { unescapeFromProjection } from './document_text_index';
import {
  hasLiftedClauseHtml,
  LIFT_PLACEHOLDER_ATTRIBUTE,
} from './clausebook_insert';

// Mirrors DIFF_CLASSES in lease_editor_controller.js:9294. Only the members
// this module writes are duplicated here.
const DIFF_CLASSES = {
  ITEM: 'diff-item',
  INSERTION: 'diff-item--insertion',
  PRIMARY: 'diff-item--primary',
  ACCEPTED: 'diff-item--accepted',
  PROCESSING: 'diff-item--processing',
};

function cleanDummyHtml(dummy, isListItemChange) {
  const indentElement = dummy.querySelector('span[style*="7pt"]');
  if (indentElement) {
    while (indentElement.parentElement.firstElementChild !== indentElement) {
      indentElement.parentElement.firstElementChild.remove();
    }
    indentElement.remove();
  } else if (isListItemChange) {
    // Remove the rendered list number and the spaces following it. Numbering
    // comes from the destination template and is recalculated by the editor.
    dummy.children[0]?.remove();
    while (dummy.children[0] && dummy.children[0].textContent.trim() === '') {
      dummy.children[0].remove();
    }
  }
  return dummy;
}

/**
 * Build a Version Control block insertion from the editor's list template.
 * Both imported diffgrams and generated insert_block proposals delegate here,
 * so free-text allocation and template behavior cannot drift between paths.
 */
export async function buildVersionControlListItem(
  insertedBlock,
  diffgramItem,
  { listItemsManager, getNextFreeTextValue, getNewPid },
) {
  let level = listItemsManager.getLevelInfoByHeader(diffgramItem.ListHeader);

  if (!level && diffgramItem.Block === 'p') {
    level = {
      format: '',
      level: 'paragraph',
      listHeader: '',
      name: '',
      outerTag: 'P',
      outline: '',
      previewText: '',
    };
  }

  const useIncomingHtml = () => {
    const randomPid = getNewPid();
    insertedBlock[0].innerHTML = `<div pid="${randomPid}" style="">${diffgramItem.Html}</div>`;
    diffgramItem._isBlocked = true;
    diffgramItem._blockedReason =
      'Inserting a list-item from an unknown format is not supported. To add a new list item use the "Add List" button in the toolbar and mark as resolved when finished.';
    diffgramItem._pid = randomPid;
  };

  if (!level) {
    useIncomingHtml();
    return;
  }

  const headerFreeTextId = getNextFreeTextValue();
  const bodyFreeTextId = getNextFreeTextValue();
  diffgramItem._listLevel = level;
  diffgramItem._listHeaderTextId = headerFreeTextId;
  diffgramItem._listBodyTextId = bodyFreeTextId;

  const listItem = await listItemsManager.asyncCreateListItemFromTemplate(
    level.level,
    level.format,
    level.name,
    headerFreeTextId,
    bodyFreeTextId,
  );

  if (level.level === 'paragraph') {
    const paragraphElement = listItem.querySelector('p');
    paragraphElement.setAttribute('style', diffgramItem.BlockStyle || '');
  }

  let dummyHtml = document.createElement('dummy');
  dummyHtml.innerHTML = diffgramItem.Html;

  diffgramItem._bodyElement = listItem.querySelector(
    `[free-text="${bodyFreeTextId}"]`,
  );
  diffgramItem._headerElement = listItem.querySelector(
    `[free-text="${headerFreeTextId}"]`,
  );
  diffgramItem._expectedParagraph = listItem.querySelector('table')
    ? Boolean(diffgramItem._bodyElement && diffgramItem._headerElement)
    : false;

  const isListItemChange = Boolean(diffgramItem.ListHeader);
  const freeTextsInTemplate = listItem.querySelectorAll('[free-text]');
  if (freeTextsInTemplate.length !== 1 && !listItem.querySelector('table')) {
    freeTextsInTemplate[1].parentElement.remove();
  }

  dummyHtml = cleanDummyHtml(
    dummyHtml,
    isListItemChange && level.level !== 'paragraph',
  );
  let appendElementsTo =
    diffgramItem._headerElement || diffgramItem._bodyElement;
  let appendBefore = appendElementsTo.querySelector('.EOCE');
  let useTableTemplate = false;

  if (listItem.querySelector('table')) {
    const incomingCells = dummyHtml.querySelectorAll('td');
    const templateCells = listItem.querySelectorAll('td');

    if (
      incomingCells.length === 2 &&
      diffgramItem._bodyElement &&
      diffgramItem._headerElement &&
      incomingCells.length === templateCells.length
    ) {
      useTableTemplate = true;
      diffgramItem._expectedParagraph = false;

      incomingCells.forEach((cell, cellIndex) => {
        appendElementsTo =
          cellIndex === 0
            ? diffgramItem._headerElement
            : diffgramItem._bodyElement;
        appendBefore = appendElementsTo.querySelector('.EOCE');
        Array.from(cell.children).forEach(node => {
          node.remove();
          appendBefore.parentElement.insertBefore(node, appendBefore);
        });
      });
    } else {
      useIncomingHtml();
      return;
    }
  }

  if (!useTableTemplate) {
    Array.from(dummyHtml.children).forEach(node => {
      node.remove();
      appendBefore.parentElement.insertBefore(node, appendBefore);
    });
  }

  Array.from(listItem.children).forEach(node => {
    node.remove();
    insertedBlock[0].appendChild(node);
  });

  const newPid = insertedBlock[0].querySelector('[pid]');
  insertedBlock[0].classList.add(
    DIFF_CLASSES.ACCEPTED,
    DIFF_CLASSES.PROCESSING,
  );
  diffgramItem._pid = newPid.getAttribute('pid');
}

// The editor never places a block marker flush against its anchor: markers
// already attached to the same anchor sit between them, and the legacy import
// path (lease_editor_controller.js:11020) walks past them before inserting.
// Without this walk two insertions sharing one anchor land in reverse proposal
// order, because `anchor.after()` always inserts immediately after the anchor.
function landingSiteAfter(anchor) {
  let cursor = anchor;
  while (
    cursor.nextElementSibling &&
    ['ins', 'del', 'ignore'].includes(
      cursor.nextElementSibling.tagName.toLowerCase(),
    )
  ) {
    cursor = cursor.nextElementSibling;
  }
  return cursor.closest('[diff-type="block"]') || cursor;
}

/**
 * Materialize one routed insert_block proposal as a Version Control block
 * marker placed after `anchor`, the existing `[pid]` element the locator
 * resolved. Returns `{ marker, item }` on success, or `{ blocked }` when the
 * configured list template cannot carry the block — the caller turns that into
 * a drafter note rather than failing the whole batch.
 *
 * This lives outside the controller so the production path is the path under
 * test (spec/js/README.md, "Extract first").
 */
export async function buildGeneratedBlockInsertion(
  { anchor, proposal, index },
  { listItemsManager, getNextFreeTextValue, getNewPid },
) {
  const marker = document.createElement('ins');
  marker.className = `${DIFF_CLASSES.ITEM} ${DIFF_CLASSES.INSERTION} ${DIFF_CLASSES.PRIMARY}`;
  // Acceptance dispatch (lease_editor_controller.js:8332) routes an `ins`
  // carrying a non-empty `list-header` inside `.lease--original` to
  // annotationsHelper.actions.acceptListItem, which writes orderedListitem and
  // reruns the numbering pass. Carrying the attribute is what makes renumbering
  // and cross-reference refresh consequences of accepting this one change
  // rather than separately authored changes.
  marker.setAttribute('list-header', 'paragraph');
  marker.setAttribute('diff-item', `diffgram.items[${index}]`);
  marker.setAttribute('diff-index', String(index));
  marker.setAttribute('diff-pid', anchor.getAttribute('pid') || '');
  marker.setAttribute('diff-type', 'block');
  marker.setAttribute('ng-click', `annotationsHelper.jumpToRequest(${index})`);

  // Model prose is assigned through textContent, so it can never become
  // executable or template markup. A Clausebook lift carries the same text and
  // is additionally marked: after Angular links the contenteditable,
  // integrateGeneratedInsertBlocks swaps that span for the held HTML through
  // lp:clausebook:insert rather than creating a second markup insertion path.
  // If that swap cannot happen the block still reads as the clause, which is
  // the outcome every non-lifted block already has.
  const content = document.createElement('span');
  content.textContent = unescapeFromProjection(proposal.content);
  if (hasLiftedClauseHtml(proposal)) {
    content.setAttribute(LIFT_PLACEHOLDER_ATTRIBUTE, '');
  }
  const item = {
    Html: content.outerHTML,
    Block: 'p',
    BlockStyle: anchor.getAttribute('style') || '',
    ListHeader: 'paragraph',
    Nested: -1,
    inTable: false,
    _change: 'block',
    _position: 'after',
  };

  await buildVersionControlListItem([marker], item, {
    listItemsManager,
    getNextFreeTextValue,
    getNewPid,
  });

  if (item._isBlocked) {
    return {
      blocked: {
        reason: 'unsupported_list_template',
        message: item._blockedReason,
      },
    };
  }

  marker.setAttribute('consolidated-pid', item._pid);
  landingSiteAfter(anchor).after(marker);
  item._node = marker;

  // Numbering is derived from structure. The builder marks the block accepted
  // so this pass counts it, then the marker reverts to unresolved — the same
  // sequence the import path runs at lease_editor_controller.js:10087. No
  // review item is allocated for the numbers or for any cross-reference text
  // that acceptance later refreshes.
  listItemsManager.createListItemsMap();
  marker.classList.remove(DIFF_CLASSES.ACCEPTED, DIFF_CLASSES.PROCESSING);

  return { marker, item };
}
